import { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Badge from '../base/Badge';
import Button from '../base/Button';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import SearchBox from '../common/SearchBox';
import { drumSearchKey, matchesSearch } from '../../helpers/cableSearch';
import { useProductionMaterialBreakdown } from '../../features/jobs/hooks/useProductionDashboardStats';

// Which products a material bar is made of.
//
// The two charts on the production dashboard answer "how much copper is the
// factory using" and nothing about where it is going. Clicking a bar opens this:
// every active product drawing on that material, down to the task and the drum,
// with what the recipe planned against what the counter actually recorded.
//
// The thing it is really for is the LAST column. A drum that ran long eats
// material nobody planned for, and until now the only way to find it was to open
// products one at a time. Over-runs sort to the top and are the only red on the
// screen, so the answer is the first row.

const PHASE_LABEL = {
  unwinding: 'Unwind',
  pre_stranding: 'Pre-Strand',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Inner sheath',
  armouring: 'Armour',
  exterior_sheat: 'Outer sheath',
  coiling: 'Coil',
  rewinding: 'Rewind'
};

const COLOR_HEX = {
  BLUE: '#2f7cff',
  BROWN: '#8b5a2b',
  BLACK: '#222222',
  GREY: '#7a7a7a',
  GRAY: '#7a7a7a',
  'GREEN-YELLOW': '#9acd32',
  GREEN: '#2e8b57',
  YELLOW: '#f0c400',
  RED: '#d9534f',
  WHITE: '#e5e5e5',
  ORANGE: '#f39c12'
};

const kg = n =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
const m = n =>
  Number.isFinite(Number(n)) ? Math.round(Number(n)).toLocaleString('ro-RO') : '—';

/** Over by more than a kilo — below that it is rounding, not an over-run. */
const OVER_KG = 1;

const statusBg = s => {
  const u = String(s || '').toUpperCase();
  if (u === 'ACTIVE' || u === 'RUNNING') return 'success';
  if (u === 'COMPLETED' || u === 'DONE' || u === 'FINISHED') return 'primary';
  if (u === 'PENDING') return 'warning';
  return 'secondary';
};

/** Planned vs executed for one material, as a line somebody can read at a glance. */
const MaterialTotal = ({ total }) => {
  const planned = Number(total.planned_kg) || 0;
  const executed = Number(total.executed_kg) || 0;
  const delta = executed - planned;
  const over = delta > OVER_KG;
  const pct = planned > 0 ? Math.min(100, (executed / planned) * 100) : 0;

  // The task rows and the job summaries are two different arrays that should
  // agree. When they do not, say so — a table quietly showing less than the bar
  // it was opened from is the worst of the three possible behaviours.
  const rowsExec = Number(total.rows_executed_kg);
  const drift =
    Number.isFinite(rowsExec) && Math.abs(rowsExec - executed) > OVER_KG
      ? rowsExec - executed
      : 0;

  return (
    <div className="mb-3">
      <div className="d-flex flex-wrap align-items-baseline gap-2 mb-1">
        <span className="fw-bold fs-8">{total.material}</span>
        <Badge variant="phoenix" bg="secondary" className="fs-10">
          {total.materialType}
        </Badge>
        <span className="ms-auto font-monospace fs-9">
          <span className="text-700">{kg(executed)}</span>
          <span className="text-700"> / {kg(planned)} kg</span>
        </span>
        <Badge
          variant="phoenix"
          bg={over ? 'danger' : delta < -OVER_KG ? 'info' : 'success'}
          className="fs-10"
        >
          {delta > 0 ? '+' : ''}
          {kg(delta)} kg
        </Badge>
      </div>
      <ProgressBar
        now={pct}
        variant={over ? 'danger' : 'success'}
        style={{ height: 6 }}
        className="bg-body-tertiary"
      />
      {drift !== 0 && (
        <div className="fs-10 text-warning mt-1">
          The per-drum rows add up to {kg(rowsExec)} kg, {kg(Math.abs(drift))} kg{' '}
          {drift > 0 ? 'more' : 'less'} than the chart total — the job summary and
          the tasks have drifted apart.
        </div>
      )}
    </div>
  );
};

const MaterialDrilldownModal = ({ materialKeys = [], allMaterials = [], onToggleKey, onHide }) => {
  const keys = (Array.isArray(materialKeys) ? materialKeys : []).filter(Boolean);
  const show = keys.length > 0;
  const { data, isLoading, isError, error } = useProductionMaterialBreakdown(keys);

  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');

  // The modal stays mounted between bars — `show` is just "a bar is selected".
  // Without this, yesterday's search would still be in the box the next time a
  // bar is clicked, and the table would open empty for no visible reason.
  useEffect(() => {
    if (!show) {
      setStatus('all');
      setQuery('');
    }
  }, [show]);

  const allRows = useMemo(() => {
    const list = data?.rows || [];
    return [...list]
      .map(r => {
        const over_kg = (Number(r.executed_kg) || 0) - (Number(r.planned_kg) || 0);
        const over_m = (Number(r.executedLength_m) || 0) - (Number(r.plannedLength_m) || 0);
        // Computed once per row rather than per keystroke: every spelling of the
        // cable ("3x25+16", "3 x 25 + 16", "3x2,5") plus the drum, phase,
        // machine and material. Same rule the Projects table searches by.
        return { ...r, over_kg, over_m, isOver: over_kg > OVER_KG, searchKey: drumSearchKey(r) };
      })
      // Over-runs first, biggest first: the row somebody opened this for is the
      // top one. Then by product, so a product's drums stay together.
      .sort((a, b) => {
        if (a.isOver !== b.isOver) return a.isOver ? -1 : 1;
        if (a.isOver && b.isOver) return b.over_kg - a.over_kg;
        return String(a.productLabel || '').localeCompare(String(b.productLabel || ''));
      });
  }, [data]);

  // The variants come from the DATA, not from a list written here. A task status
  // nobody anticipated (CANCELLED, BLOCKED) then gets a chip of its own instead
  // of being quietly unreachable, and each chip carries the count so an empty
  // filter is visible before it is clicked.
  const statusVariants = useMemo(() => {
    const counts = new Map();
    for (const r of allRows) {
      const s = String(r.taskStatus || 'UNKNOWN').toUpperCase();
      counts.set(s, (counts.get(s) || 0) + 1);
    }
    const order = ['ACTIVE', 'RUNNING', 'PENDING', 'COMPLETED', 'DONE', 'FINISHED'];
    return [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => {
        const ia = order.indexOf(a.value);
        const ib = order.indexOf(b.value);
        if (ia !== ib) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        return a.value.localeCompare(b.value);
      });
  }, [allRows]);

  const statusChips = useMemo(
    () => [
      { value: 'all', label: `All (${allRows.length})`, bg: 'secondary' },
      ...statusVariants.map(v => ({
        value: v.value,
        label: `${v.value} (${v.count})`,
        bg: statusBg(v.value)
      }))
    ],
    [allRows.length, statusVariants]
  );

  const rows = useMemo(
    () =>
      allRows.filter(
        r =>
          (status === 'all' || String(r.taskStatus || 'UNKNOWN').toUpperCase() === status) &&
          matchesSearch(r.searchKey, query)
      ),
    [allRows, status, query]
  );

  const overCount = rows.filter(r => r.isOver).length;
  const filtered = rows.length !== allRows.length;

  // What is actually on screen. With a filter on, the totals above belong to the
  // whole bar and the table does not add up to them any more — so say what the
  // visible rows come to rather than leave the two numbers to be compared.
  const viewExecuted = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.executed_kg) || 0), 0),
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'productLabel',
        header: 'Product',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column">
            <Link
              to={`/apps/project-management/project-details/${original.jobId}`}
              className="fw-semibold text-decoration-none"
              onClick={onHide}
            >
              {original.productLabel || '—'}
            </Link>
            <span className="fs-10 text-700">{original.jobCode || ''}</span>
          </div>
        ),
        meta: { headerProps: { style: { width: '18%' } }, cellProps: { className: 'py-2' } }
      },
      {
        accessorKey: 'material',
        header: 'Material',
        cell: ({ row: { original } }) => (
          <span className="fs-10">{original.material}</span>
        ),
        meta: { headerProps: { style: { width: '10%' } } }
      },
      {
        accessorKey: 'phase',
        header: 'Phase',
        cell: ({ row: { original } }) => (
          <div className="d-flex align-items-center gap-2">
            <span className="fs-9">
              {PHASE_LABEL[original.phase] || original.phase || '—'}
            </span>
            {COLOR_HEX[String(original.color || '').toUpperCase()] && (
              <span
                title={original.color}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: COLOR_HEX[String(original.color).toUpperCase()],
                  border: '1px solid rgba(255,255,255,0.2)',
                  display: 'inline-block'
                }}
              />
            )}
          </div>
        ),
        meta: { headerProps: { style: { width: '12%' } } }
      },
      {
        accessorKey: 'machineId',
        header: 'Machine',
        cell: ({ row: { original } }) => (
          <span className="fs-10 text-uppercase">{original.machineId || '—'}</span>
        ),
        meta: { headerProps: { style: { width: '8%' } } }
      },
      {
        accessorKey: 'tokenId',
        header: 'Drum',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column">
            <span className="font-monospace fs-10">{original.tokenId || '—'}</span>
            {original.drum_material && (
              <span className="fs-10 text-700">
                {String(original.drum_material).toUpperCase()}
                {original.drum_diameter ? ` Ø${original.drum_diameter}` : ''}
              </span>
            )}
          </div>
        ),
        meta: { headerProps: { style: { width: '12%' } } }
      },
      {
        id: 'length',
        header: 'Cable (m)',
        accessorFn: row => row.executedLength_m,
        cell: ({ row: { original } }) => {
          const long = original.over_m > 0.5;
          return (
            <div className="text-end font-monospace fs-10">
              <span className={long ? 'text-danger fw-bold' : ''}>
                {m(original.executedLength_m)}
              </span>
              <span className="text-700"> / {m(original.plannedLength_m)}</span>
            </div>
          );
        },
        meta: {
          headerProps: { style: { width: '13%', textAlign: 'end' } },
          cellProps: { className: 'text-end' }
        }
      },
      {
        id: 'weight',
        header: 'Material (kg)',
        accessorFn: row => row.executed_kg,
        cell: ({ row: { original } }) => (
          <div className="text-end font-monospace fs-10">
            <span className={original.isOver ? 'text-danger fw-bold' : ''}>
              {kg(original.executed_kg)}
            </span>
            <span className="text-700"> / {kg(original.planned_kg)}</span>
          </div>
        ),
        meta: {
          headerProps: { style: { width: '13%', textAlign: 'end' } },
          cellProps: { className: 'text-end' }
        }
      },
      {
        id: 'over',
        header: 'Over',
        accessorFn: row => row.over_kg,
        cell: ({ row: { original } }) =>
          original.isOver ? (
            <div className="text-end">
              <Badge variant="phoenix" bg="danger" className="fs-10">
                +{kg(original.over_kg)} kg
              </Badge>
              {original.over_m > 0.5 && (
                <div className="fs-10 text-danger">+{m(original.over_m)} m</div>
              )}
            </div>
          ) : (
            <div className="text-end fs-10 text-700">—</div>
          ),
        meta: {
          headerProps: { style: { width: '10%', textAlign: 'end' } },
          cellProps: { className: 'text-end' }
        }
      },
      {
        accessorKey: 'taskStatus',
        header: 'Status',
        cell: ({ row: { original } }) => (
          <Badge variant="phoenix" bg={statusBg(original.taskStatus)} className="fs-10">
            {String(original.taskStatus || '—').toUpperCase()}
          </Badge>
        ),
        meta: { headerProps: { style: { width: '10%' } } }
      }
    ],
    [onHide]
  );

  const table = useAdvanceTable({
    data: rows,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true
  });

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">
          Material in production
          {overCount > 0 && (
            <Badge variant="phoenix" bg="danger" className="ms-2 fs-10">
              {overCount} over plan
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Every material on this chart, so one bar can be compared with its
            neighbours without closing and re-clicking. */}
        {allMaterials.length > 1 && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {allMaterials.map(mat => {
              const on = keys.includes(mat.materialKey);
              return (
                <Button
                  key={mat.materialKey}
                  size="sm"
                  variant={on ? 'primary' : 'phoenix-secondary'}
                  onClick={() => onToggleKey?.(mat.materialKey)}
                  title={on ? 'Remove from the table' : 'Add to the table'}
                >
                  {mat.material}
                </Button>
              );
            })}
          </div>
        )}

        {isLoading && (
          <div className="d-flex align-items-center gap-2 py-4 justify-content-center">
            <Spinner animation="border" size="sm" />
            <span className="fs-9">Reading every active product…</span>
          </div>
        )}

        {isError && (
          <Alert variant="phoenix-danger" className="py-2 fs-9">
            Could not load the breakdown: {error?.message || 'unknown error'}
          </Alert>
        )}

        {!isLoading && !isError && (
          <>
            {(data?.totals || []).map(t => (
              <MaterialTotal key={t.materialKey} total={t} />
            ))}

            {/* Find a cable the way it is spoken: "CYYF 3 x 25 + 16", "3x25+16",
                "3x25", "25" and "cyyf 25" all reach the same product. */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <SearchBox
                placeholder="Product, conductors, section…"
                size="sm"
                value={query}
                onChange={e => setQuery(e.target.value)}
                // SearchBox puts its input in a bare <form>, so Enter would
                // submit it and reload the page — closing the modal and losing
                // the bar that was clicked. Filtering is live; Enter has nothing
                // left to do.
                onKeyDown={e => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
                style={{ maxWidth: 260 }}
              />
              {/* "All" is the first chip rather than a special case, so there is
                  one render path and the counts cannot be formatted two ways. */}
              <div className="d-flex flex-wrap gap-1 ms-sm-auto">
                {statusChips.map(c => (
                  <Button
                    key={c.value}
                    size="sm"
                    variant={status === c.value ? 'primary' : `phoenix-${c.bg}`}
                    className="fs-10"
                    onClick={() => setStatus(status === c.value ? 'all' : c.value)}
                  >
                    {c.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="fs-10 text-700 mb-2">
              {filtered ? (
                <>
                  {rows.length} of {allRows.length} drums shown —{' '}
                  <span className="font-monospace">{kg(viewExecuted)} kg</span> executed
                  in view.{' '}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 fs-10 align-baseline"
                    onClick={() => {
                      setStatus('all');
                      setQuery('');
                    }}
                  >
                    Clear filters
                  </Button>
                </>
              ) : (
                <>
                  {rows.length} {rows.length === 1 ? 'drum' : 'drums'} across{' '}
                  {data?.jobs || 0} active {data?.jobs === 1 ? 'product' : 'products'}.
                  Executed figures come from the machine counter; the plan comes
                  from the standard.
                </>
              )}
            </div>

            {!rows.length ? (
              allRows.length ? (
                // The difference matters: one of these is a bar with nothing
                // behind it, the other is a filter that hid everything.
                <Alert variant="phoenix-info" className="py-2 fs-9 mb-0">
                  None of the {allRows.length} drums on this material match{' '}
                  {[query ? `“${query}”` : null, status !== 'all' ? `status ${status}` : null]
                    .filter(Boolean)
                    .join(' at ')}
                  .
                </Alert>
              ) : (
                <Alert variant="phoenix-info" className="py-2 fs-9 mb-0">
                  No active product is drawing on this material. The bar is made of
                  job-level totals only — nothing is queued or running against it
                  right now.
                </Alert>
              )
            ) : (
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }}
                />
                <AdvanceTableFooter pagination />
              </AdvanceTableProvider>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MaterialDrilldownModal;
export { OVER_KG };
